import './GtfsEditorStyles.css';
import BlockUi from '@availity/block-ui';
import * as d3 from 'd3';
import * as React from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Form, Header } from 'semantic-ui-react';
import * as actions from '../../../actions/actions';
import busStopIcon from '../../../static/bus-stop-20.png';
import { PolylineProps, PushpinProps } from '../../../types/MapModels';
import AzureMapSelectionMap from '../../shared/AzureMap/AzureMapSelectionMap';
import GtfsTable, { GtfsTableHandle } from './GtfsTable';
import ZipLoader, { FileData } from './ZipLoader';

const styles = {
    map: {
        width: '100%',
        height: '522px',
    } as React.CSSProperties,
};

interface Props {
    fileNameSource: string | undefined;
}

export interface RowData {
    [key: string]: string;
}

const GtfsEditorForm: React.FC<Props> = ({ fileNameSource }) => {
    const [filesState, setFilesState] = useState<FileData[]>([]);
    const [isVisibleMapState, setIsVisibleMapState] = useState<boolean>(false);
    const [polylinesState, setPolylinesState] = useState<PolylineProps[]>();
    const [pushpinsState, setPushpinsState] = useState<PushpinProps[]>([]);
    const [formBlockingState, setFormBlockingState] = useState(false);
    const tripIdRef = useRef<string | undefined>(undefined);
    const tableRefs = useRef<Record<string, GtfsTableHandle | null>>({});

    useEffect(() => {
        if (filesState === undefined) return;
        setFormBlockingState(false);
    }, [filesState]);

    useEffect(() => {
        setFormBlockingState(fileNameSource !== undefined);
    }, [fileNameSource]);

    const cloneRows = (rows: FileData['data'], mapRow?: (row: RowData) => RowData) => {
        const nextRows = rows.map((row) => (mapRow ? mapRow(row as RowData) : { ...(row as RowData) })) as typeof rows;
        nextRows.columns = [...rows.columns];
        return nextRows;
    };

    const sortFiles = (files: FileData[]) => [...files].sort((a, b) => a.sort > b.sort ? -1 : 1);

    const handleFilesChange = async (files: FileData[]) => {
        const processedFiles = await processData(files);
        setFilesState(sortFiles(processedFiles));
    };

    async function addDateTime(files: FileData[]) {
        if (fileNameSource === undefined) {
            return files;
        }

        const tripTimes = await actions.getTripTimes(fileNameSource);
        if (!tripTimes) {
            return files;
        }

        const tripTimesMap = new Map(tripTimes.map((tripTime) => [tripTime.tripId, `${tripTime.startTime} - ${tripTime.endTime}`]));

        return files.map((file) => {
            if (file.fileName !== 'trips.txt') {
                return file;
            }

            return {
                ...file,
                data: cloneRows(file.data, (row) => ({
                    ...row,
                    times: tripTimesMap.get(row.trip_id) ?? row.times,
                })),
            };
        });
    }

    const cloneFiles = (files: FileData[]) => files.map((file) => ({
        ...file,
        data: cloneRows(file.data),
    }));

    const processData = async (files: FileData[]) => {
        let nextFiles = cloneFiles(files);
        nextFiles = addStopBayName(nextFiles);
        nextFiles = await addDateTime(nextFiles);
        return nextFiles;
    };

    const groupByToMap = <T, Q>(array: T[] | undefined, predicate: (value: T, index: number, array: T[]) => Q) =>
        array?.reduce((map, value, index, array) => {
            const key = predicate(value, index, array);
            map.get(key)?.push(value) ?? map.set(key, [value]);
            return map;
        }, new Map<Q, T[]>());

    const addStopBayName = (files: FileData[]) => {
        const tripsBayTable = files.find((file) => file.fileName === 'trips_bays.txt');
        const stopsTable = files.find((file) => file.fileName === 'stops.txt');

        if (!tripsBayTable || !stopsTable) {
            return files;
        }

        const tripsBayTableData = tripsBayTable?.data;
        const tripsBayTableDataMap2 = groupByToMap(tripsBayTableData, s => s.STOP_ID as string);
        const stopsTableData = stopsTable?.data;
        const stopsTableDataMap = new Map(stopsTableData?.map(s => [s.stop_id, s]));
        const additionalStops: RowData[] = [];

        for (const [key, values] of tripsBayTableDataMap2?.entries() ?? []) {
            const stop = stopsTableDataMap.get(key);
            if (stop) {
                const stop_bays = new Set(values.map(s => s.STOP_BAY));
                stop_bays.forEach(stopBay => {
                    additionalStops.push({
                        ...(stop as RowData),
                        stop_bays: stopBay ?? '',
                    });
                });
            }
        }

        const nextStopsData = [...stopsTable.data, ...additionalStops] as typeof stopsTable.data;
        nextStopsData.columns = [...stopsTable.data.columns];

        return files.map((file) => file.fileName === 'stops.txt'
            ? { ...file, data: nextStopsData }
            : file);
    };

    const stopProcessed = (rowData: RowData, value: string) => {
        drawAllRoutesForStop(rowData, value);
    };

    const drawAllRoutesForStop = (rowData: RowData, value: string) => {
        const stopTimesFileData = filesState.find(s => s.fileName === 'stop_times.txt')?.data;
        const stopTimes = stopTimesFileData?.filter(s => s.stop_id === value) ?? [];
        const tripIds = Array.from(d3.group(stopTimes, s => s.trip_id), ([key, _value]) => key);
        const tripsFileData = filesState.find(s => s.fileName === 'trips.txt')?.data;
        const trips = tripsFileData?.filter(x => tripIds.includes(x.trip_id)) ?? [];
        const shapesFileData = filesState.find(s => s.fileName === 'shapes.txt')?.data;
        const groups = d3.group(trips, s => s.trip_id);
        const polylines: PolylineProps[] = [];
        for (const group of groups) {
            const shapeIds = group[1].map(x => x.shape_id);
            const shapes = shapesFileData?.filter(s => shapeIds.includes(s.shape_id)) ?? [];
            const coordPairs: CoordinatePair[] = shapes.map(({ shape_pt_lat, shape_pt_lon }) => [shape_pt_lat as unknown as Latitude, shape_pt_lon as unknown as Longitude]);
            polylines.push({
                locations: coordPairs,
                options: {
                    strokeColor: 'blue',
                    strokeThickness: 4,
                },
            });
        }
        setPolylinesState(polylines);
        setPushpinsState([{
            location: [rowData.stop_lat as unknown as Latitude, rowData.stop_lon as unknown as Longitude],
            options: {
                icon: busStopIcon,
                catId: 'bus-stop',
            },
        }]);
        setIsVisibleMapState(true);

        const tripsForFilters = tripIds
            .filter((x): x is string => x !== undefined)
            .filter((value, index, self) => self.indexOf(value) === index);
        filterTrips(tripsForFilters);

        const routesForFilters = trips.map(x => x.route_id)
                .filter((x): x is string => x !== undefined)
                .filter((value, index, self) => self.indexOf(value) === index)
            ?? [];
        filterRoutes(routesForFilters);
    };

    const tripProcessed = (rowData: RowData, value: string) => {
        const shapesFileData = filesState.find(s => s.fileName === 'shapes.txt')?.data;
        const shape_id = rowData['shape_id'];
        if (!shape_id) {
            return;
        }

        const shapes = shapesFileData?.filter(s => s.shape_id === shape_id) ?? [];
        const coordPairs: CoordinatePair[] = shapes.map(({ shape_pt_lat, shape_pt_lon }) => [shape_pt_lat as unknown as Latitude, shape_pt_lon as unknown as Longitude]);
        setPolylinesState([{
            locations: coordPairs,
            options: {
                strokeColor: 'red',
                strokeThickness: 4,
            },
        }]);
        const stopTimesFileData = filesState.find(s => s.fileName === 'stop_times.txt')?.data;
        const stopTimes = stopTimesFileData?.filter(s => s.trip_id === value) ?? [];
        const stopIds = stopTimes.map(s => s.stop_id);
        const stopsFileData = filesState.find(s => s.fileName === 'stops.txt')?.data;
        const stops = stopsFileData?.filter(s => stopIds.includes(s.stop_id)) ?? [];
        setPushpinsState(
            stops.map(({ stop_lat, stop_lon }) => ({
                location: [stop_lat as unknown as Latitude, stop_lon as unknown as Longitude],
                options: {
                    icon: busStopIcon,
                    catId: 'bus-stop',
                },
            })),
        );
        setIsVisibleMapState(true);
    };

    const mapProcessed = (rowData: RowData, fieldName: string, value: string) => {
        switch (fieldName) {
            case 'stop_id':
                stopProcessed(rowData, value);
                break;
            case 'trip_id':
                tripProcessed(rowData, value);
                break;
        }
    };

    const onClickClear = () => {
        Object.values(tableRefs.current).forEach((table) => table?.clearFilters());
        setPolylinesState([]);
        setPushpinsState([]);
        tripIdRef.current = undefined;
    };

    const onCellClick = (rowData: RowData, fieldName: string, value: string) => {
        Object.values(tableRefs.current).forEach((table) => table?.setFilterValue(fieldName, value));
        mapProcessed(rowData, fieldName, value);
        if (fieldName === 'trip_id') {
            tripIdRef.current = value;
            filterStop('stop_id');
        }
        if(fieldName === 'stop_bays') {
            const tripIds: string[] = getTripIdsByStopBay(rowData.stop_id, rowData.stop_bays);
            filterTrips(tripIds);
        }
    };

    function getTripIdsByStopBay(stopId: string, stopBay: string) : string[] {
        if (!filesState)
            return [];
        const tripsBaysFileData = filesState.find(s => s.fileName === 'trips_bays.txt')?.data;
        const tripsBays = tripsBaysFileData?.filter(s => s.STOP_BAY === stopBay && s.STOP_ID === stopId) ?? [];
        return tripsBays.map(s => s.TRIP_ID ?? '');
    }

    const filterStop = (fieldName: string) => {
        if (!filesState)
            return;
        const tripId = tripIdRef.current;
        const stopTimesFileData = filesState.find(s => s.fileName === 'stop_times.txt')?.data;
        const stopTimes = stopTimesFileData?.filter(s => s.trip_id === tripId) ?? [];
        const stopIds = stopTimes.filter(s => s.stop_id).map(s => s.stop_id ?? '');
        const targetTable = tableRefs.current['stops.txt'];
        targetTable?.setMultiplyValueFilter(fieldName, stopIds);
    };

    const filterTrips = (tripIds: string[]) => {
        const targetTable = tableRefs.current['trips.txt'];
        targetTable?.setMultiplyValueFilter('trip_id', tripIds);
    };

    const filterRoutes = (routeIds: string[]) => {
        const targetTable = tableRefs.current['routes.txt'];
        targetTable?.setMultiplyValueFilter('route_id', routeIds);
    };

    return (
        <Fragment>
            <Header as="h1" className="reportHeader">
                GTFS Viewer.
            </Header>
            {fileNameSource && <h4>File name: {fileNameSource}</h4>}
            <BlockUi tag="div" blocking={formBlockingState}>
                <Form className="gtfsEditor">
                    <Form.Group widths="equal" inline className="inputGroup">
                        <Form.Field>
                            <ZipLoader onFilesChange={handleFilesChange}
                                       displayLoader={setFormBlockingState}
                                       fileNameSource={fileNameSource}/>
                        </Form.Field>
                    </Form.Group>
                    {isVisibleMapState && <Form.Group>
                        <Form.Field style={{ width: '100%' }}>
                            <div style={styles.map}>
                                <AzureMapSelectionMap
                                    showZoomControls={true}
                                    pushpinsProp={pushpinsState}
                                    polylinesProp={polylinesState}
                                />
                            </div>
                        </Form.Field>
                    </Form.Group>}
                    {filesState.length > 0 && <Form.Group className="bottomGroup">
                        <Form.Field style={{ width: '100%' }}>
                            <label className="categoryHeader">
                                <div>Tables</div>
                            </label>
                            <Button
                                content="Clear filters"
                                onClick={onClickClear}
                            />
                            <div>
                                {filesState.filter(f => f.visible > 0)
                                    .map((file) => {
                                            return <GtfsTable key={file.fileName}
                                                              {...file}
                                                              ref={(table) => {
                                                                  tableRefs.current[file.fileName] = table;
                                                              }}
                                                              onCellClick={onCellClick}
                                            />;
                                        },
                                    )}
                            </div>
                        </Form.Field>
                    </Form.Group>}
                </Form>
            </BlockUi>
        </Fragment>
    );
};

export default GtfsEditorForm;
